import React, { useMemo } from 'react';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { AddRounded, DeleteOutlineRounded, EditRounded, RemoveRounded, ShoppingCartRounded } from '@mui/icons-material';

const money = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const weight = new Intl.NumberFormat('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export default function Cart({ items = [], onAumentar, onDisminuir, onEditarPeso, onEliminar }) {
  const resumen = useMemo(() => items.reduce((acc, item) => {
    if (item?.pesable) acc.kg += Number(item?.cantidad || 0);
    else acc.unidades += Number(item?.cantidad || 0);
    return acc;
  }, { unidades: 0, kg: 0 }), [items]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', bgcolor: '#fff' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 1.65, py: 1.25, borderBottom: '1px solid #e2e8f0', '@media (max-height: 820px)': { py: 0.9 } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 23, fontWeight: 1000, lineHeight: 1.05, color: '#15253a' }}>
            Venta actual
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Ticket en proceso
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.55} alignItems="center">
          <Chip size="small" label={`${items.length} ítem(s)`} sx={{ bgcolor: '#edf4ff', color: '#1769c9', fontWeight: 850 }} />
          {resumen.unidades > 0 ? (
            <Chip size="small" label={`${resumen.unidades} un.`} sx={{ bgcolor: '#f3f6fa', color: '#51667d', fontWeight: 850 }} />
          ) : null}
          {resumen.kg > 0 ? (
            <Chip size="small" label={`${weight.format(resumen.kg)} kg`} sx={{ bgcolor: '#ecfdf3', color: '#15803d', fontWeight: 850 }} />
          ) : null}
        </Stack>
      </Stack>

      {items.length > 0 ? (
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: 'minmax(0,1fr) 112px 94px 34px',
            gap: 0.8,
            px: 1.35,
            py: 0.65,
            bgcolor: '#f4f7fb',
            borderBottom: '1px solid #e5ebf2',
          }}
        >
          <Typography variant="caption" fontWeight={900} color="text.secondary">PRODUCTO</Typography>
          <Typography variant="caption" fontWeight={900} color="text.secondary" textAlign="center">CANT.</Typography>
          <Typography variant="caption" fontWeight={900} color="text.secondary" textAlign="right">SUBTOTAL</Typography>
          <Box />
        </Box>
      ) : null}

      <Box
        className="pos-cart-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'scroll',
          px: 1.05,
          py: 0.75,
          pr: 0.65,
          scrollbarGutter: 'stable',
          scrollbarWidth: 'auto',
          scrollbarColor: '#759dcc #e8eef5',
          '&::-webkit-scrollbar': { width: 13 },
          '&::-webkit-scrollbar-track': { bgcolor: '#e8eef5', borderRadius: 999 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#759dcc', borderRadius: 999, border: '3px solid #e8eef5' },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#4f7eb6' },
        }}
      >
        {!items.length ? (
          <Box sx={{ minHeight: 350, height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center', color: 'text.secondary', p: 2 }}>
            <Box>
              <ShoppingCartRounded sx={{ fontSize: 60, opacity: 0.13, color: '#3f648a' }} />
              <Typography variant="h6" fontWeight={900} color="text.primary">La venta está vacía</Typography>
              <Typography variant="body2">Escanee un producto o selecciónelo desde el catálogo.</Typography>
            </Box>
          </Box>
        ) : (
          items.map((item, index) => (
            <Box
              key={`${item.id || item.sku}-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) 112px 94px 34px' },
                alignItems: 'center',
                gap: 0.8,
                px: 0.5,
                py: 0.8,
                borderBottom: '1px solid #e9eef4',
                '&:hover': { bgcolor: '#f9fbfd' },
                '@media (max-height: 820px)': { py: 0.55 },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: '#f3f6fa',
                    display: 'grid',
                    placeItems: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                    border: '1px solid #edf1f5',
                  }}
                >
                  {item.imagen ? (
                    <Box component="img" src={item.imagen} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <ShoppingCartRounded sx={{ color: '#a9b7c6' }} />
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.55} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={950} noWrap sx={{ color: '#17263a' }}>{item.nombre}</Typography>
                    {item?.pesable ? <Chip size="small" label="Pesable" variant="outlined" sx={{ height: 19, fontSize: 9.5, color: '#168447', borderColor: '#bfe8cf' }} /> : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {item?.pesable
                      ? `${weight.format(Number(item.cantidad || 0))} kg × ${money.format(item.precioBruto)}/kg`
                      : `SKU: ${item.sku || item.codigoBarras || 'Sin código'} · ${money.format(item.precioBruto)} c/u`}
                  </Typography>
                </Box>
              </Stack>

              {item?.pesable ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditRounded fontSize="small" />}
                  onClick={() => onEditarPeso?.(index)}
                  sx={{ minWidth: 100, height: 32, fontWeight: 850, borderColor: '#cfdceb' }}
                >
                  {weight.format(Number(item.cantidad || 0))} kg
                </Button>
              ) : (
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                  <IconButton size="small" onClick={() => onDisminuir?.(index)} sx={{ border: '1px solid #d7e0ea', width: 30, height: 30, borderRadius: 1.2 }}>
                    <RemoveRounded fontSize="small" />
                  </IconButton>
                  <Typography sx={{ width: 32, textAlign: 'center', fontWeight: 950 }}>{item.cantidad}</Typography>
                  <IconButton size="small" onClick={() => onAumentar?.(index)} sx={{ border: '1px solid #d7e0ea', width: 30, height: 30, borderRadius: 1.2 }}>
                    <AddRounded fontSize="small" />
                  </IconButton>
                </Stack>
              )}

              <Typography variant="body2" fontWeight={1000} textAlign="right" sx={{ color: '#15253a' }}>
                {money.format(item.precioBruto * item.cantidad)}
              </Typography>

              <IconButton color="error" size="small" onClick={() => onEliminar?.(index)} sx={{ justifySelf: 'end' }}>
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
